<template>
  <NewPackChip
    :current-game-slug="currentGameSlug"
    v-if="isNew === 1 && sliderChip"
    variant="green"
  />

  <NuxtLinkLocale
    @click="sendDataLayer"
    class="game_pack_card t-white d-flex-column space-between align-start"
    :class="[
      currentGameSlug,
      props.disabled && 'no_pointer',
      props.slug,
      props.isComingSoon && 'disable',
      isStadium && 'disable',
    ]"
    :to="link"
    @mouseenter.native="$isMobile() ? null : handleArrowEvent('inside')"
    @mouseleave.native="$isMobile() ? null : handleArrowEvent('outside')"
  >
    <NewPackChip
      :current-game-slug="currentGameSlug"
      v-if="isNew === 1 && !sliderChip"
      variant="green"
    />

    <div class="game_pack_card_disable_mask" v-if="props.isComingSoon">
      <label
        class="disable_pill t-white font-family-1 regular-xl b-radius-9 f-w-600"
        >{{ $t("coming_soon.coming_soon") }}</label
      >
    </div>
    <div class="game_pack_card_disable_mask d-flex-column" v-if="isStadium">
      <label
        class="disable_pill t-white font-family-1 regular-xl b-radius-9 f-w-600"
        >{{ $t("coming_soon.coming_soon") }}<br /><span
          class="font-family-2 regular-l"
          >April 22, 2025</span
        ></label
      >
    </div>
    <div class="d-flex-column justify-start align-start">
      <div class="d-flex-row space-between align-start">
        <h4 class="bold-pack title font-family-2" :style="{ width: width }">
          {{ props.title }}
        </h4>

        <div class="pack_index medium-s t-white font-family-1">
          0{{ props.index + 1 }}.
        </div>
      </div>

      <ul class="perk_list" v-if="raids.length <= 0">
        <li
          class="t-light regular-pack font-family-2"
          v-for="perk in props.perks"
        >
          {{ perk.title }}
        </li>
      </ul>

      <div
        class="d-flex-row justify-start gap-10 flex-wrap sm-row-to-col align-md-start sm-hide"
        v-if="props.raids.length > 0"
      >
        <label class="t-white font-family-2 medium-xl">{{
          $t("new.next_raid_in")
        }}</label>
        <div
          class="raid_chip medium-l t-white font-family-2"
          v-for="(raid, index) in props.raids"
          :key="raid"
          :class="[`index-${index + 1}`]"
        >
          <clock-icon v-if="index === 0" />
          {{ raid.start_time }}
        </div>
      </div>
      <div
        class="d-flex-row justify-start gap-10 flex-wrap sm-row-to-col align-md-start above-sm-hide"
        v-if="props.raids.length > 0"
      >
        <label class="t-white font-family-2 medium-xl">{{
          $t("new.next_raid_in")
        }}</label>
        <div class="d-flex-row justify-start">
          <div
            class="raid_chip medium-l t-white font-family-2"
            v-for="(raid, index) in raids"
            :key="raid"
            :class="[`index-${index + 1}`]"
          >
            <clock-icon v-if="index === 0" />
            {{ raid.start_time }}
          </div>
        </div>
      </div>
    </div>

    <NuxtImg
      :src="props.image"
      :alt="props.title + ' Game pack image'"
      class="pack_image"
      :class="[
        link === '/marvel-rivals-boost/marvel-rivals-win-boost' && 'top',
        ['rematch-boost', 'clash-royale-boost'].includes(currentGameSlug) &&
          'large_image',
      ]"
      fit="contain"
      v-if="props.image"
    />

    <div class="arrow_right" ref="arrow_right">
      <h4
        class="t-white bold-s f-w-600 font-family-1 text white-space-no flex-4"
        ref="arrow_right_text"
      >
        {{ $t("main.buy_now") }}
      </h4>
      <arrow-right />
    </div>
  </NuxtLinkLocale>
</template>

<script setup>
import { useGtm } from "@gtm-support/vue-gtm";
import ArrowRight from "../icons/ArrowRight.vue";
import ClockIcon from "../icons/ClockIcon.vue";
import NewPackChip from "./NewPackChip.vue";
import { computed, ref } from "vue";

const { getUser } = useUserStore();

const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  perks: {
    default: [],
  },
  index: {
    type: Number,
    default: 0,
  },
  image: {
    type: String,
    default: "",
  },
  link: {
    type: String,
    default: "",
  },
  id: {
    type: Number,
    default: 0,
  },
  fullTitle: {
    type: String,
    default: "",
  },
  slug: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isComingSoon: {
    default: false,
  },
  raids: {
    default: [],
  },
  currentGameSlug: {
    default: "",
  },
  isNew: {
    default: 0,
  },
  sliderChip: {
    default: false,
  },
});

const { public: publicConfig } = useRuntimeConfig();
const { nuxtEnv } = publicConfig;
const { currentGamePackSlug } = useGameComposable();
const $gtm = useGtm();
const width = computed(() => {
  if (!props?.title) {
    return "0ch";
  }
  if (props.title.split(" ").length > 2) {
    return "9ch";
  }
  if (props.title.length >= 20) {
    return "11ch";
  }
  if (props.title.length >= 18) {
    return "10ch";
  }
  if (props.title.length >= 16) {
    return "9ch";
  }
  if (props.title.length > 14) {
    return "8ch";
  }
  if (props.title.length >= 13) {
    return "10ch";
  }
  if (props.title.length >= 10) {
    return "8ch";
  }

  return "7ch";
});
const arrow_right = ref(null);
const arrow_right_text = ref(null);
const sendDataLayer = async () => {
  if (nuxtEnv === "dev" && window.dataLayer?.push) {
    let otherParams = {};
    if (getUser.id) {
      otherParams = {
        user_data: {
          first_name: getUser.username,
          email_address: getUser.email,
          user_id: getUser.id,
          customer_lifetime_spent: getUser.total_spent,
        },
      };
    }
    window.dataLayer?.push({
      event: "select_item",
      ecommerce: {
        items: [
          {
            item_id: "game_pack_" + props.id,
            item_name: props.fullTitle,
            affiliation: props.slug,
            quantity: 1,
          },
        ],
      },
      ...otherParams,
    });
  }
};
const handleArrowEvent = (prompt) => {
  // if (device) {
  //   return;
  // }
  const el = arrow_right.value;
  const width = el?.getBoundingClientRect();
  const elText = arrow_right_text.value;
  const elWidth = elText?.getBoundingClientRect();
  if (prompt === "outside") {
    el.style.width = "36px";
  }
  if (prompt === "inside") {
    const calWidth = elWidth?.width + width?.width + 12;
    el.style.width = `${calWidth}px`;
  }
};
const isStadium = computed(() => {
  return false;
});
</script>
